import { supabase } from '@/integrations/supabase/client';
import { Sentry, isSentryEnabled } from '@/sentry';

/**
 * Wrapper around supabase.functions.invoke that:
 * 1. Reads the response body before throwing so Sentry receives the real
 *    failure reason (e.g. IDLE_TIMEOUT) instead of the generic SDK message.
 * 2. Captures every edge function error in Sentry automatically, so callers
 *    don't need to call Sentry.captureException manually.
 */
export async function invokeEdgeFunction<T = unknown>(
  functionName: string,
  options?: Parameters<typeof supabase.functions.invoke>[1]
): Promise<T> {
  const { data, error } = await supabase.functions.invoke<T>(functionName, options);

  if (error) {
    try {
      const body = await (error as any).context?.json?.();
      if (body) (error as any).responseBody = body;
    } catch { /* response body unreadable, throw original error as-is */ }

    if (isSentryEnabled) Sentry.captureException(error);
    throw error;
  }

  return data as T;
}
